import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { 
    Bell, Send, Users, Trophy, GraduationCap, Globe, 
    Image, Link as LinkIcon, AlertTriangle, CheckCircle, 
    Clock, Trash2, Eye, RefreshCw, BarChart3
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PRIORITY_OPTIONS = [
    { value: 'low', label: 'Low', color: 'bg-gray-500/20 text-gray-400' },
    { value: 'normal', label: 'Normal', color: 'bg-blue-500/20 text-blue-400' },
    { value: 'high', label: 'High', color: 'bg-amber-500/20 text-amber-400' },
    { value: 'urgent', label: 'Urgent', color: 'bg-red-500/20 text-red-400' }
];

const TARGET_OPTIONS = [
    { value: 'all', label: 'All Users', icon: Globe, description: 'Send to everyone' },
    { value: 'tournament', label: 'Tournament Participants', icon: Trophy, description: 'Users registered for a tournament' },
    { value: 'athletes', label: 'All Athletes', icon: Users, description: 'All registered athletes' },
    { value: 'schools', label: 'School Athletes', icon: GraduationCap, description: 'Athletes from a specific school' }
];

const NotificationsTab = ({ tournaments = [], schools = [] }) => {
    const [notifications, setNotifications] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showComposeDialog, setShowComposeDialog] = useState(false);
    const [showPreviewDialog, setShowPreviewDialog] = useState(false);
    const [selectedNotification, setSelectedNotification] = useState(null);
    const [sending, setSending] = useState(false);
    
    // Form state
    const [formData, setFormData] = useState({
        title: '',
        message: '',
        link: '',
        image_url: '',
        priority: 'normal',
        target_type: 'all',
        target_id: '',
        target_name: ''
    });

    useEffect(() => {
        loadNotifications();
        loadStats();
    }, []);

    const loadNotifications = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_URL}/api/notifications/admin/history`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setNotifications(response.data.notifications || []);
        } catch (error) {
            console.error('Failed to load notifications:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadStats = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_URL}/api/notifications/admin/stats`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setStats(response.data);
        } catch (error) {
            console.error('Failed to load stats:', error);
        }
    };

    const handleSendNotification = async () => {
        if (!formData.title.trim() || !formData.message.trim()) {
            toast.error('Title and message are required');
            return;
        }

        if (formData.target_type === 'tournament' && !formData.target_id) {
            toast.error('Please select a tournament');
            return;
        }

        if (formData.target_type === 'schools' && !formData.target_id) {
            toast.error('Please select a school');
            return;
        }

        setSending(true);
        try {
            const token = localStorage.getItem('token');
            const payload = {
                title: formData.title,
                message: formData.message,
                priority: formData.priority,
                target_type: formData.target_type,
                ...(formData.link && { link: formData.link }),
                ...(formData.image_url && { image_url: formData.image_url }),
                ...(formData.target_id && { target_id: formData.target_id }),
                ...(formData.target_name && { target_name: formData.target_name })
            };

            const response = await axios.post(`${API_URL}/api/notifications/send`, payload, {
                headers: { Authorization: `Bearer ${token}` }
            });

            toast.success(response.data.message || 'Notification sent successfully!');
            setShowComposeDialog(false);
            resetForm();
            loadNotifications();
            loadStats();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to send notification');
        } finally {
            setSending(false);
        }
    };

    const handleDeleteNotification = async (notificationId) => {
        if (!window.confirm('Are you sure you want to delete this notification?')) return;

        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${API_URL}/api/notifications/admin/${notificationId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Notification deleted');
            loadNotifications();
            loadStats();
        } catch (error) {
            toast.error('Failed to delete notification');
        }
    };

    const resetForm = () => {
        setFormData({
            title: '',
            message: '',
            link: '',
            image_url: '',
            priority: 'normal',
            target_type: 'all',
            target_id: '',
            target_name: ''
        });
    };

    const handleTargetChange = (value) => {
        setFormData(prev => ({
            ...prev,
            target_type: value,
            target_id: '',
            target_name: ''
        }));
    };

    const handleTargetIdChange = (value) => {
        let targetName = '';
        if (formData.target_type === 'tournament') {
            const tournament = tournaments.find(t => t.id === value);
            targetName = tournament?.name || '';
        } else if (formData.target_type === 'schools') {
            const school = schools.find(s => s.id === value);
            targetName = school?.school_name || school?.name || '';
        }
        setFormData(prev => ({
            ...prev,
            target_id: value,
            target_name: targetName
        }));
    };

    const getPriorityBadge = (priority) => {
        const opt = PRIORITY_OPTIONS.find(p => p.value === priority);
        return opt ? opt.color : 'bg-gray-500/20 text-gray-400';
    };

    const getTargetLabel = (targetType, targetName) => {
        const opt = TARGET_OPTIONS.find(t => t.value === targetType);
        if (targetType === 'all') return 'All Users';
        return targetName ? `${opt?.label}: ${targetName}` : opt?.label || targetType;
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('en-GB', {
            day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
        });
    };

    return (
        <div className="space-y-6">
            {/* Stats Cards */}
            {stats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-[#0d2847] border border-white/5 p-4">
                        <Bell className="w-5 h-5 text-primary mb-2" />
                        <p className="stat-display text-2xl">{stats.total_notifications_sent}</p>
                        <p className="text-xs text-muted-foreground">Total Sent</p>
                    </div>
                    <div className="bg-[#0d2847] border border-white/5 p-4">
                        <Users className="w-5 h-5 text-success mb-2" />
                        <p className="stat-display text-2xl">{stats.total_push_subscriptions}</p>
                        <p className="text-xs text-muted-foreground">Push Subscribers</p>
                    </div>
                    <div className="bg-[#0d2847] border border-white/5 p-4">
                        <Clock className="w-5 h-5 text-amber-400 mb-2" />
                        <p className="stat-display text-2xl">{stats.notifications_this_week}</p>
                        <p className="text-xs text-muted-foreground">This Week</p>
                    </div>
                    <div className="bg-[#0d2847] border border-white/5 p-4">
                        <BarChart3 className="w-5 h-5 text-purple-400 mb-2" />
                        <p className="stat-display text-2xl">{stats.by_target_type?.all || 0}</p>
                        <p className="text-xs text-muted-foreground">Broadcasts</p>
                    </div>
                </div>
            )}

            {/* Actions Bar */}
            <div className="flex flex-wrap gap-4 p-4 bg-[#0d2847] border border-white/5 items-center">
                <Button
                    onClick={() => setShowComposeDialog(true)}
                    className="bg-primary hover:bg-cyan-400 text-[#0a1628]"
                    data-testid="compose-notification-btn"
                >
                    <Send className="w-4 h-4 mr-2" />
                    Compose Notification
                </Button>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { loadNotifications(); loadStats(); }}
                    className="border-white/10"
                >
                    <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                    Refresh
                </Button>
                <span className="text-sm text-muted-foreground ml-auto">
                    {notifications.length} notifications sent
                </span>
            </div>

            {/* Notifications History Table */}
            <div className="bg-[#0d2847] border border-white/5 overflow-x-auto">
                <table className="w-full">
                    <thead className="bg-[#0d2847]">
                        <tr>
                            <th className="text-left text-xs tracking-widest uppercase text-muted-foreground p-4">Title</th>
                            <th className="text-left text-xs tracking-widest uppercase text-muted-foreground p-4">Target</th>
                            <th className="text-left text-xs tracking-widest uppercase text-muted-foreground p-4">Priority</th>
                            <th className="text-left text-xs tracking-widest uppercase text-muted-foreground p-4">Push Status</th>
                            <th className="text-left text-xs tracking-widest uppercase text-muted-foreground p-4">Sent By</th>
                            <th className="text-left text-xs tracking-widest uppercase text-muted-foreground p-4">Date</th>
                            <th className="text-right text-xs tracking-widest uppercase text-muted-foreground p-4">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {notifications.map((notif) => (
                            <tr key={notif.id} className="border-t border-white/5 hover:bg-white/5">
                                <td className="p-4">
                                    <div>
                                        <p className="font-medium">{notif.title}</p>
                                        <p className="text-xs text-muted-foreground line-clamp-1">{notif.message}</p>
                                    </div>
                                </td>
                                <td className="p-4">
                                    <span className="text-sm">
                                        {getTargetLabel(notif.target_type, notif.target_name)}
                                    </span>
                                </td>
                                <td className="p-4">
                                    <span className={`px-2 py-1 text-xs uppercase rounded ${getPriorityBadge(notif.priority)}`}>
                                        {notif.priority}
                                    </span>
                                </td>
                                <td className="p-4">
                                    {notif.push_sent ? (
                                        <div className="flex items-center gap-2">
                                            <CheckCircle className="w-4 h-4 text-success" />
                                            <span className="text-xs text-muted-foreground">
                                                {notif.push_success_count} sent
                                                {notif.push_fail_count > 0 && `, ${notif.push_fail_count} failed`}
                                            </span>
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-2">
                                            <Clock className="w-4 h-4 text-amber-400" />
                                            <span className="text-xs text-muted-foreground">Pending</span>
                                        </div>
                                    )}
                                </td>
                                <td className="p-4 text-sm text-muted-foreground">{notif.sender_name || 'Admin'}</td>
                                <td className="p-4 text-xs text-muted-foreground">{formatDate(notif.created_at)}</td>
                                <td className="p-4">
                                    <div className="flex items-center justify-end gap-1">
                                        <Button 
                                            size="sm" 
                                            variant="ghost" 
                                            onClick={() => { setSelectedNotification(notif); setShowPreviewDialog(true); }}
                                            className="text-muted-foreground hover:text-white"
                                        >
                                            <Eye className="w-4 h-4" />
                                        </Button>
                                        <Button 
                                            size="sm" 
                                            variant="ghost" 
                                            onClick={() => handleDeleteNotification(notif.id)}
                                            className="text-destructive hover:text-destructive"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {notifications.length === 0 && (
                    <div className="text-center py-12 text-muted-foreground">
                        {loading ? 'Loading notifications...' : 'No notifications sent yet'}
                    </div>
                )}
            </div>

            {/* Compose Notification Dialog */}
            <Dialog open={showComposeDialog} onOpenChange={setShowComposeDialog}>
                <DialogContent className="bg-[#0d2847] border border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl uppercase flex items-center gap-2">
                            <Send className="w-5 h-5 text-primary" />
                            Compose Notification
                        </DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-4">
                        {/* Target Selection */}
                        <div>
                            <label className="text-xs text-muted-foreground uppercase tracking-wider">Target Audience</label>
                            <div className="grid grid-cols-2 gap-2 mt-2">
                                {TARGET_OPTIONS.map(opt => (
                                    <button
                                        key={opt.value}
                                        onClick={() => handleTargetChange(opt.value)}
                                        className={`p-3 border text-left transition-colors ${
                                            formData.target_type === opt.value 
                                                ? 'border-primary bg-primary/10' 
                                                : 'border-white/10 hover:border-white/20'
                                        }`}
                                    >
                                        <opt.icon className={`w-4 h-4 mb-1 ${formData.target_type === opt.value ? 'text-primary' : 'text-muted-foreground'}`} />
                                        <p className="text-sm font-medium">{opt.label}</p>
                                        <p className="text-xs text-muted-foreground">{opt.description}</p>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Tournament/School Selection */}
                        {formData.target_type === 'tournament' && (
                            <div>
                                <label className="text-xs text-muted-foreground uppercase tracking-wider">Select Tournament</label>
                                <Select value={formData.target_id} onValueChange={handleTargetIdChange}>
                                    <SelectTrigger className="bg-[#0a1628] border-white/10 mt-1">
                                        <SelectValue placeholder="Choose a tournament" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#0d2847] border-white/10">
                                        {tournaments.map(t => (
                                            <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}

                        {formData.target_type === 'schools' && (
                            <div>
                                <label className="text-xs text-muted-foreground uppercase tracking-wider">Select School</label>
                                <Select value={formData.target_id} onValueChange={handleTargetIdChange}>
                                    <SelectTrigger className="bg-[#0a1628] border-white/10 mt-1">
                                        <SelectValue placeholder="Choose a school" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#0d2847] border-white/10 max-h-48">
                                        {schools.map(s => (
                                            <SelectItem key={s.id} value={s.id}>{s.school_name || s.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}

                        {/* Title */}
                        <div>
                            <label className="text-xs text-muted-foreground uppercase tracking-wider">Title *</label>
                            <Input
                                value={formData.title}
                                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                                placeholder="Notification title"
                                className="bg-[#0a1628] border-white/10 mt-1"
                                maxLength={100}
                            />
                        </div>

                        {/* Message */}
                        <div>
                            <label className="text-xs text-muted-foreground uppercase tracking-wider">Message *</label>
                            <Textarea
                                value={formData.message}
                                onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                                placeholder="Write your notification message..."
                                className="bg-[#0a1628] border-white/10 mt-1 min-h-[100px]"
                                maxLength={500}
                            />
                            <p className="text-xs text-muted-foreground mt-1">{formData.message.length}/500 characters</p>
                        </div>

                        {/* Priority */}
                        <div>
                            <label className="text-xs text-muted-foreground uppercase tracking-wider">Priority</label>
                            <Select value={formData.priority} onValueChange={(v) => setFormData(prev => ({ ...prev, priority: v }))}>
                                <SelectTrigger className="bg-[#0a1628] border-white/10 mt-1">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-[#0d2847] border-white/10">
                                    {PRIORITY_OPTIONS.map(opt => (
                                        <SelectItem key={opt.value} value={opt.value}>
                                            <span className={`px-2 py-0.5 rounded text-xs ${opt.color}`}>{opt.label}</span>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Optional: Link */}
                        <div>
                            <label className="text-xs text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                                <LinkIcon className="w-3 h-3" /> Link (Optional)
                            </label>
                            <Input
                                value={formData.link}
                                onChange={(e) => setFormData(prev => ({ ...prev, link: e.target.value }))}
                                placeholder="https://example.com/page"
                                className="bg-[#0a1628] border-white/10 mt-1"
                            />
                        </div>

                        {/* Optional: Image */}
                        <div>
                            <label className="text-xs text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                                <Image className="w-3 h-3" /> Image URL (Optional)
                            </label>
                            <Input
                                value={formData.image_url}
                                onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))}
                                placeholder="https://example.com/image.jpg"
                                className="bg-[#0a1628] border-white/10 mt-1"
                            />
                        </div>

                        {formData.priority === 'urgent' && (
                            <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded">
                                <AlertTriangle className="w-4 h-4 text-red-400" />
                                <p className="text-xs text-red-400">Urgent notifications will require user interaction to dismiss</p>
                            </div>
                        )}
                    </div>

                    <DialogFooter className="mt-4">
                        <Button variant="outline" onClick={() => { setShowComposeDialog(false); resetForm(); }} className="border-white/10">
                            Cancel
                        </Button>
                        <Button 
                            onClick={handleSendNotification} 
                            disabled={sending || !formData.title || !formData.message}
                            className="bg-primary hover:bg-cyan-400 text-[#0a1628]"
                        >
                            {sending ? (
                                <>
                                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                    Sending...
                                </>
                            ) : (
                                <>
                                    <Send className="w-4 h-4 mr-2" />
                                    Send Notification
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Preview Dialog */}
            <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
                <DialogContent className="bg-[#0d2847] border border-white/10 text-white max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl uppercase">Notification Details</DialogTitle>
                    </DialogHeader>
                    {selectedNotification && (
                        <div className="space-y-4">
                            <div>
                                <p className="text-xs text-muted-foreground">Title</p>
                                <p className="font-medium text-lg">{selectedNotification.title}</p>
                            </div>
                            <div>
                                <p className="text-xs text-muted-foreground">Message</p>
                                <p className="text-sm">{selectedNotification.message}</p>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-xs text-muted-foreground">Target</p>
                                    <p className="text-sm">{getTargetLabel(selectedNotification.target_type, selectedNotification.target_name)}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-muted-foreground">Priority</p>
                                    <span className={`px-2 py-1 text-xs uppercase rounded ${getPriorityBadge(selectedNotification.priority)}`}>
                                        {selectedNotification.priority}
                                    </span>
                                </div>
                            </div>
                            {selectedNotification.link && (
                                <div>
                                    <p className="text-xs text-muted-foreground">Link</p>
                                    <a href={selectedNotification.link} target="_blank" rel="noopener noreferrer" className="text-primary text-sm hover:underline">
                                        {selectedNotification.link}
                                    </a>
                                </div>
                            )}
                            {selectedNotification.image_url && (
                                <div>
                                    <p className="text-xs text-muted-foreground">Image</p>
                                    <img src={selectedNotification.image_url} alt="Notification" className="mt-1 max-h-32 rounded" />
                                </div>
                            )}
                            <div className="pt-4 border-t border-white/10">
                                <div className="grid grid-cols-2 gap-4 text-xs">
                                    <div>
                                        <p className="text-muted-foreground">Sent By</p>
                                        <p>{selectedNotification.sender_name}</p>
                                    </div>
                                    <div>
                                        <p className="text-muted-foreground">Date</p>
                                        <p>{formatDate(selectedNotification.created_at)}</p>
                                    </div>
                                    <div>
                                        <p className="text-muted-foreground">Push Sent</p>
                                        <p>{selectedNotification.push_success_count} successful</p>
                                    </div>
                                    <div>
                                        <p className="text-muted-foreground">Push Failed</p>
                                        <p>{selectedNotification.push_fail_count} failed</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default NotificationsTab;
